import React, { ChangeEvent, useEffect, useRef, useState } from "react";

// STYLES
import "./ForgotPasswordCode.scss";

// COMPONENTS
import GenericButton from "../../ui/buttons/GenericButtons/GenericButton";
import LoadingSpinner from "../../ui/modals/Spinners/LoadingSpinner";

// HOOKS
import { useAuthContext } from "../../../hooks/context/AuthContext";
import GenericModal from "../../ui/modals/GenericModal/GenericModal";

interface IForgotPasswordCodeProps {
    userEmail: string;
    onPasswordResetSuccess: () => void;
    onCancel: () => void;
}

const passwordResetModalContentMap: Record<
    string,
    { title: string; body: string }
> = {
    1: {
        title: "Password Updated!",
        body: "Let's log in!",
    },
    2: {
        title: "Error",
        body: "An error was encountered sending your code. Please try again later.",
    },
};

const codeResetModalContentMap: Record<
    string,
    { title: string; body: string }
> = {
    1: {
        title: "Code Sent!",
        body: "Check your email for your unique code and come back here to reset your password",
    },
    2: {
        title: "Error",
        body: "An error has occurred. Please try again later.",
    },
};

const ForgotPasswordCode: React.FC<IForgotPasswordCodeProps> = ({
    userEmail,
    onPasswordResetSuccess,
    onCancel,
}) => {
    const { postForgotPasswordAndSendCode, postCodeAndResetPassword } =
        useAuthContext();

    const [pwdError, setPwdError] = useState("");
    const [codeError, setCodeError] = useState("");
    const [codeState, setCodeState] = useState<string | null>(null);
    const [pwd1State, setPwd1State] = useState<string | null>(null);
    const [pwd2State, setPwd2State] = useState<string | null>(null);

    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

    const [showCodeSentModal, setShowCodeSentModal] = useState(false);
    const [codeModalState, setCodeModalState] = useState(1);

    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [passwordResetModalState, setPasswordResetModalState] = useState(1); //1 -> success, 0 -> misc API error, -1 -> code error

    const passwordResetSuccessfully = useRef(false);

    // const codeRef = useRef<HTMLInputElement | null>(null);
    // const pwd1ref = useRef<HTMLInputElement | null>(null);
    // const pwd2ref = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setCodeError("");
    }, [codeState]);

    useEffect(() => {
        setPwdError("");
    }, [pwd1State, pwd2State]);

    const validateFormData = (): boolean => {
        if (!codeState || !pwd1State || !pwd2State) {
            if (!codeState) {
                setCodeError("This Field is Required");
            }
            if (!pwd1State || !pwd2State) {
                setPwdError("Missing Required Password Field");
            }
            return false;
        } else if (pwd1State?.length < 6) {
            setPwdError("Password is not long enough");
            return false;
        } else if (pwd1State !== pwd2State) {
            setPwdError("Passwords do not match");
            return false;
        } else {
            // limit of what we can validate on client before sending API call
            setPwdError("");
            setCodeError("");
            return true;
        }
    };

    const onSubmitPasswordReset = (): void => {
        const formDataValid = validateFormData();
        if (formDataValid) {
            const onSuccess = () => {
                passwordResetSuccessfully.current = true;
                setShowLoadingSpinner(false);
                setShowPasswordResetModal(true);
            };
            const onFail = (error: string) => {
                setShowLoadingSpinner(false);
                if (error === "Invalid code") {
                    setCodeError("Invalid Code");
                } else {
                    setPasswordResetModalState(2);
                    setShowPasswordResetModal(true);
                }
                // this could be an API error, but we specifically need to check for an invalid code
                // error because that will need to be called out specifically, apart from other API errors.
                // TODO - check with Zach about pwd handling
            };
            // After validation we know that codeState and pwd1State are non-null and as validated as they can be on the client side
            setShowLoadingSpinner(true);
            postCodeAndResetPassword(
                userEmail,
                codeState!!,
                pwd1State!!,
                onSuccess,
                onFail,
            );
        } else {
        }
    };

    return (
        <div className="ForgotPasswordCode">
            <LoadingSpinner
                className="ForgotPasswordCode__OpaqueSpinner"
                raised={showLoadingSpinner}
            />
            <GenericModal
                className="ForgotPasswordCode__CodeModal"
                show={showCodeSentModal}
                onHide={() => setShowCodeSentModal(false)}
                header={codeResetModalContentMap[codeModalState].title}
                body={<p>{codeResetModalContentMap[codeModalState].body}</p>}
                footer={
                    <GenericButton
                        text="OK"
                        onClick={() => {
                            setShowCodeSentModal(false);
                        }}
                    />
                }
            />
            <GenericModal
                className="ForgotPasswordCode__PwdModal"
                show={showPasswordResetModal}
                onHide={() => {
                    setShowPasswordResetModal(false);
                    if (passwordResetModalState === 1) {
                        onPasswordResetSuccess();
                    }
                }}
                header={
                    passwordResetModalContentMap[passwordResetModalState].title
                }
                body={
                    <p>
                        {
                            passwordResetModalContentMap[
                                passwordResetModalState
                            ].body
                        }
                    </p>
                }
                footer={
                    <GenericButton
                        text="OK"
                        onClick={() => {
                            setShowPasswordResetModal(false);
                            if (passwordResetModalState === 1) {
                                onPasswordResetSuccess();
                            }
                        }}
                    />
                }
            />
            <div className="ForgotPasswordCode__Wrapper">
                <div className="ForgotPasswordCode__Header">
                    <h2>Reset Password</h2>
                </div>
                <div className="ForgotPasswordCode__Body">
                    <h5>Password Reset Code</h5>
                    <p className="subtext">
                        Enter the code that was emailed to you.
                    </p>
                    <input
                        type="number"
                        placeholder="123456"
                        autoComplete="one-time-code"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const v = e.target.value;
                            setCodeState(v);
                        }}
                    />
                    <p className="ForgotPasswordCode__BodyError">{codeError}</p>

                    <GenericButton
                        className="ForgotPasswordCode__GetNewCode"
                        text="Get a New Code"
                        onClick={() => {
                            setShowLoadingSpinner(true);
                            const onSuccess = () => {
                                setShowLoadingSpinner(false);
                                setShowCodeSentModal(true);
                                setCodeModalState(1);
                            };
                            const onFail = () => {
                                setShowLoadingSpinner(false);
                                setShowCodeSentModal(true);
                                setCodeModalState(2);
                            };

                            postForgotPasswordAndSendCode(
                                userEmail,
                                onSuccess,
                                onFail,
                            );
                        }}
                    />

                    <h5 style={{ marginTop: "0.75rem" }}>New Password</h5>
                    <p className="subtext">
                        Create a new password. 6 character min.
                    </p>
                    <input
                        type="password"
                        placeholder="New Password"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const v = e.target.value;
                            setPwd1State(v);
                        }}
                    />
                    <h5 style={{ marginTop: "0.75rem" }}>
                        Confirm New Password
                    </h5>
                    <input
                        type="password"
                        placeholder="Confirm New Password"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const v = e.target.value;
                            setPwd2State(v);
                        }}
                    />
                    <p className="ForgotPasswordCode__BodyError">{pwdError}</p>
                </div>
                <div
                    className="ForgotPasswordCode__Footer"
                    style={{ marginTop: "0.75rem" }}
                >
                    <GenericButton
                        className="ForgotPasswordCode__Cancel"
                        text="Reset Password"
                        onClick={() => {
                            onSubmitPasswordReset();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordCode;
